import React from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Mail, Phone, MapPin, Clock } from "lucide-react";
import EscudoNacional from "./common/EscudoNacional";

const Footer = () => {
  const { t } = useTranslation();
  const { lang } = useParams();
  const cur = lang === "en" ? "en" : "pe";

  return (
    <footer
      data-testid="site-footer"
      style={{ background: "var(--pum-ink)", color: "#C6D2E5" }}
    >
      <div className="pum-flag-ribbon" aria-hidden="true" />
      <div
        className="pum-container"
        style={{
          padding: "4rem 1.25rem 2rem",
          display: "grid",
          gridTemplateColumns: "1.4fr 1fr 1fr 1.2fr",
          gap: "2.5rem",
        }}
      >
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.85rem", marginBottom: "1rem" }}>
            <EscudoNacional size={44} />
            <span
              style={{
                fontFamily: "var(--font-serif)",
                fontWeight: 900,
                fontSize: "1.4rem",
                color: "#fff",
              }}
            >
              {t("brand.name")}
            </span>
          </div>
          <p style={{ fontSize: "0.88rem", lineHeight: 1.65, color: "#9AACC9" }}>
            {t("footer.about")}
          </p>
          <div
            style={{
              marginTop: "1.25rem",
              padding: "0.6rem 0.85rem",
              background: "rgba(217,30,24,0.12)",
              borderLeft: "3px solid var(--pum-red)",
              fontSize: "0.78rem",
              color: "#F4C7C5",
            }}
          >
            {t("footer.flag")}
          </div>
        </div>

        <div>
          <h4 style={{ color: "#fff", fontSize: "0.95rem", fontFamily: "var(--font-sans)", marginBottom: "1rem", textTransform: "uppercase", letterSpacing: "0.14em" }}>
            {t("footer.navTitle")}
          </h4>
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "0.6rem" }}>
            <li><Link data-testid="footer-nav-home" to={`/${cur}`} style={{ fontSize: "0.9rem" }}>{t("nav.home")}</Link></li>
            <li><Link data-testid="footer-nav-privacy" to={`/${cur}/privacy`} style={{ fontSize: "0.9rem" }}>{t("nav.privacy")}</Link></li>
            <li><Link data-testid="footer-nav-cookies" to={`/${cur}/cookies`} style={{ fontSize: "0.9rem" }}>{t("nav.cookies")}</Link></li>
            <li><Link data-testid="footer-nav-terms" to={`/${cur}/terms`} style={{ fontSize: "0.9rem" }}>{t("nav.terms")}</Link></li>
          </ul>
        </div>

        <div>
          <h4 style={{ color: "#fff", fontSize: "0.95rem", fontFamily: "var(--font-sans)", marginBottom: "1rem", textTransform: "uppercase", letterSpacing: "0.14em" }}>
            {t("footer.contactTitle")}
          </h4>
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "0.75rem", fontSize: "0.88rem", lineHeight: 1.45 }}>
            <li style={{ display: "flex", gap: "0.55rem" }}>
              <MapPin size={16} style={{ flexShrink: 0, marginTop: "2px", color: "var(--pum-emerald)" }} />
              <span>{t("footer.address")}</span>
            </li>
            <li style={{ display: "flex", gap: "0.55rem" }}>
              <Phone size={16} style={{ flexShrink: 0, marginTop: "2px", color: "var(--pum-emerald)" }} />
              <a href={`tel:${t("footer.phone").replace(/\s|\(|\)/g, "")}`} data-testid="footer-phone">{t("footer.phone")}</a>
            </li>
            <li style={{ display: "flex", gap: "0.55rem" }}>
              <Mail size={16} style={{ flexShrink: 0, marginTop: "2px", color: "var(--pum-emerald)" }} />
              <a href={`mailto:${t("footer.email")}`} data-testid="footer-email">{t("footer.email")}</a>
            </li>
            <li style={{ display: "flex", gap: "0.55rem" }}>
              <Clock size={16} style={{ flexShrink: 0, marginTop: "2px", color: "var(--pum-emerald)" }} />
              <span>{t("footer.hours")}</span>
            </li>
          </ul>
        </div>

        <div>
          <h4 style={{ color: "#fff", fontSize: "0.95rem", fontFamily: "var(--font-sans)", marginBottom: "1rem", textTransform: "uppercase", letterSpacing: "0.14em" }}>
            {t("footer.legalTitle")}
          </h4>
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "0.55rem", fontSize: "0.82rem", color: "#9AACC9", lineHeight: 1.5 }}>
            <li><strong style={{ color: "#fff" }}>{t("footer.legalName")}</strong></li>
            <li>{t("footer.ruc")}</li>
            <li>{t("footer.sunarp")}</li>
            <li>{t("footer.osce")}</li>
            <li>{t("footer.license")}</li>
            <li>{t("footer.sunat")}</li>
            <li>{t("footer.jurisdiction")}</li>
            <li>{t("footer.founded")}</li>
          </ul>
        </div>
      </div>

      <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
        <div
          className="pum-container"
          style={{
            padding: "1.25rem",
            display: "flex",
            justifyContent: "space-between",
            gap: "1rem",
            flexWrap: "wrap",
            fontSize: "0.78rem",
            color: "#8296B4",
          }}
        >
          <span data-testid="footer-copyright">
            © 2026 {t("footer.legalName")}. {t("footer.rights")}
          </span>
          <span style={{ maxWidth: "560px", textAlign: "right" }}>
            {t("footer.disclaimer")}
          </span>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          [data-testid="site-footer"] > div.pum-container {
            grid-template-columns: 1fr 1fr !important;
          }
        }
        @media (max-width: 600px) {
          [data-testid="site-footer"] > div.pum-container {
            grid-template-columns: 1fr !important;
          }
        }
        [data-testid="site-footer"] a:hover { color: #fff; }
      `}</style>
    </footer>
  );
};

export default Footer;
